import { useState, useCallback, useEffect } from "react";
import { TOTAL_STEPS } from "../constants";

// ─── TYPES ──────────────────────────────────────────────────────────────────

export interface WizardData {
  // Step 1 — Primary Category
  primaryCategory: string;     // e.g. "appliances"

  // Step 2 — Subcategory / Asset Type
  subcategory: string;         // e.g. "refrigerator"
  subcategoryLabel: string;    // e.g. "Refrigerator"
  assetType: string;           // e.g. "Top Load", "Front Load"

  // Step 3 — Product Identification
  identificationMethod: "photo" | "manual" | null;
  labelPhotoFile: File | null;
  labelPhotoPreview: string;   // local object URL for preview
  extractedData: {
    brand?: string;
    model?: string;
    serialNumber?: string;
    enrichmentSteps?: { step: number; name: string; status: 'success' | 'partial' | 'failed' | 'skipped'; message: string; }[];
    dataQuality?: 'excellent' | 'good' | 'partial' | 'minimal';
    enrichmentSource?: string;
    detectedProductType?: string;
  } | null;
  isExtracting: boolean;

  // Manual / confirmed values
  brand: string;
  model: string;
  serialNumber: string;

  // Product image (fetched from lookup API or uploaded by user — NOT the label photo)
  productImageUrl: string;
  isLookingUpImage: boolean;

  // AI-enriched product fields (from analyze-label / barcode lookup)
  productTitle: string;
  productDescription: string;
  productCategory: string;
  manufacturer: string;
  mpn: string;
  barcode: string;
  productColor: string;

  // Step 4 — Details & Location
  name: string;                // auto-generated or custom
  location: string;            // room ID
  purchaseYear: number | null;
  purchaseMonth: number | null;
  purchasePrice: number | null;
  warrantyExpiresAt: string;   // ISO date string

  // Step 5 — Documents
  documents: File[];
  documentTypes: string[]; // parallel array: type per document (warranty | receipt | manual | photo | other)
}

export interface WizardActions {
  goNext: () => void;
  goBack: () => void;
  goToStep: (step: number) => void;
  updateData: (patch: Partial<WizardData>) => void;
  reset: () => void;
  canProceed: boolean;
}

const INITIAL_DATA: WizardData = {
  primaryCategory: "",
  subcategory: "",
  subcategoryLabel: "",
  assetType: "",
  identificationMethod: null,
  labelPhotoFile: null,
  labelPhotoPreview: "",
  extractedData: null,
  isExtracting: false,
  brand: "",
  model: "",
  serialNumber: "",
  productImageUrl: "",
  isLookingUpImage: false,
  productTitle: "",
  productDescription: "",
  productCategory: "",
  manufacturer: "",
  mpn: "",
  barcode: "",
  productColor: "",
  name: "",
  location: "",
  purchaseYear: null,
  purchaseMonth: null,
  purchasePrice: null,
  warrantyExpiresAt: "",
  documents: [],
  documentTypes: [],
};

const DRAFT_KEY = "homeiq_asset_draft";

// ─── HOOK ───────────────────────────────────────────────────────────────────

export function useWizardState() {
  const [currentStep, setCurrentStep] = useState(1);
  const [data, setData] = useState<WizardData>(() => {
    // Restore draft from localStorage (except File objects)
    try {
      const saved = localStorage.getItem(DRAFT_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        return {
          ...INITIAL_DATA,
          ...parsed,
          // Files can't be serialized
          labelPhotoFile: null,
          labelPhotoPreview: "",
          documents: [],
          isExtracting: false,
        };
      }
    } catch { /* ignore */ }
    return { ...INITIAL_DATA };
  });

  // Persist draft (non-File fields) whenever data changes
  useEffect(() => {
    const { labelPhotoFile: _labelPhotoFile, labelPhotoPreview: _labelPhotoPreview, documents: _documents, isExtracting: _isExtracting, ...serializable } = data;
    localStorage.setItem(DRAFT_KEY, JSON.stringify(serializable));
  }, [data]);

  const updateData = useCallback((patch: Partial<WizardData>) => {
    setData((prev) => ({ ...prev, ...patch }));
  }, []);

  const goNext = useCallback(() => {
    setCurrentStep((s) => Math.min(s + 1, TOTAL_STEPS));
  }, []);

  const goBack = useCallback(() => {
    setCurrentStep((s) => Math.max(s - 1, 1));
  }, []);

  const goToStep = useCallback((step: number) => {
    if (step >= 1 && step <= TOTAL_STEPS) setCurrentStep(step);
  }, []);

  const reset = useCallback(() => {
    setData({ ...INITIAL_DATA });
    setCurrentStep(1);
    localStorage.removeItem(DRAFT_KEY);
  }, []);

  // Step-based validation
  const canProceed = (() => {
    switch (currentStep) {
      case 1: return !!data.primaryCategory;
      case 2: return !!data.subcategory;
      case 3: return !!(data.brand || data.identificationMethod === "photo");
      case 4: return !!data.location;
      case 5: return true; // documents optional
      default: return false;
    }
  })();

  return {
    currentStep,
    data,
    goNext,
    goBack,
    goToStep,
    updateData,
    reset,
    canProceed,
  };
}
